/**
 * TemplatesPage — modern recipe gallery.
 *
 * Each recipe is a contemporary card with a generated preview plate, channel
 * tag, italic synopsis, and a small "Use" CTA. Filtering via quiet pill row.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LoadingState } from '../components/LoadingState'
import { useAuth } from '../contexts/AuthContext'
import {
  TEMPLATE_CHANNELS,
  TEMPLATES,
  templatesByChannel,
  type Template,
  type TemplateChannel,
  type TemplateSourceKind,
} from '../templates/registry'

type ChannelFilter = TemplateChannel | 'all'

const SOURCE_KIND_CARD_LABEL: Record<TemplateSourceKind, string> = {
  prompt: 'Pre-tuned prompt',
  footage: 'Bring your footage',
  photo: 'Bring a photo',
}

export function TemplatesPage() {
  // Browsing templates is PUBLIC — it's an evaluation surface. Only
  // "Use" needs an account, and the Composer's pending-compose stash
  // already survives the sign-in detour, so no gate here at all.
  const { loading } = useAuth()
  const navigate = useNavigate()
  const [channel, setChannel] = useState<ChannelFilter>('all')

  if (loading) return <LoadingState label="Loading templates" />

  const visible = templatesByChannel(channel)

  function useTemplate(t: Template) {
    navigate('/', {
      state: {
        templateSeed: {
          prompt: t.prompt,
          aspectRatio: t.aspectRatio,
          templateId: t.id,
          // Editing recipes treat footage the user already has, and photo
          // recipes animate the user's own picture. The composer needs to
          // know that before it offers to run, otherwise it fires a generate
          // on a prompt that only describes a treatment.
          sourceKind: t.sourceKind ?? 'prompt',
        },
      },
    })
  }

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <div className="pill mb-3">
            <span>Templates · {TEMPLATES.length}</span>
          </div>
          <h1 className="h-display text-[clamp(2.4rem,5.4vw,4.6rem)] text-text">
            Recipes that <span className="text-accent">already shoot well</span>.
          </h1>
          <p className="mt-4 text-text-2 text-[1.05rem] max-w-2xl">
            Pre-tuned prompts across {TEMPLATE_CHANNELS.length} channels. Pick one — the composer pre-fills, you press send.
          </p>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 mb-10">
        <FilterPill active={channel === 'all'} onClick={() => setChannel('all')} count={TEMPLATES.length}>
          All
        </FilterPill>
        {TEMPLATE_CHANNELS.map((c) => {
          const count = templatesByChannel(c.id).length
          if (count === 0) return null
          return (
            <FilterPill
              key={c.id}
              active={channel === c.id}
              onClick={() => setChannel(c.id)}
              count={count}
              title={c.description}
            >
              {c.label}
            </FilterPill>
          )
        })}
      </div>

      {visible.length === 0 && (
        <div className="card p-12 text-text-3 text-center">
          No recipes in this channel yet.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((t) => (
          <RecipeCard key={t.id} template={t} onUse={() => useTemplate(t)} />
        ))}
      </div>

      <p className="mt-16 text-center text-text-3 text-[0.92rem]">
        Want a custom recipe? <Link to="/" className="text-accent hover:underline">Write your own →</Link>
      </p>
    </section>
  )
}

/**
 * Poster-first preview: still frame in the grid, muted clip on hover / focus.
 *
 * Three deliberate choices:
 *
 * - **The <video> element is not rendered until the card is hovered.** Simply
 *   setting `preload="none"` still creates 22 media elements and, on Safari,
 *   still touches the network. Mounting on demand means an untouched grid
 *   downloads nothing but posters.
 * - **Focus counts as hover**, so keyboard users can see the preview too.
 * - **A template without a preview falls back to a typographic plate** — the
 *   title's initial in the display face — not an emoji. Emoji render
 *   differently on every OS and read as the AI default. Previews are generated
 *   per template, so the grid stays coherent while only some exist.
 */
function TemplatePreview({ template }: { template: Template }) {
  const [active, setActive] = useState(false)
  const hasVideo = Boolean(template.previewVideo)

  if (!template.posterImage && !hasVideo) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center opacity-90 select-none group-hover:scale-110 transition-transform duration-700"
        aria-hidden
      >
        <span
          className="text-[5rem] leading-none text-text/80"
          style={{
            fontVariationSettings: '"wdth" 84, "opsz" 96',
            fontWeight: 650,
            letterSpacing: '-0.04em',
          }}
        >
          {template.name.charAt(0)}
        </span>
      </div>
    )
  }

  return (
    <div
      data-testid="template-preview"
      className="absolute inset-0"
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
    >
      {template.posterImage && (
        <img
          src={template.posterImage}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover [object-position:50%_30%]"
        />
      )}
      {hasVideo && active && (
        <video
          src={template.previewVideo}
          poster={template.posterImage}
          autoPlay
          loop
          muted
          playsInline
          // Decorative: the name and description below carry the meaning, and
          // a screen reader announcing a silent loop adds nothing.
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover [object-position:50%_30%]"
        />
      )}
    </div>
  )
}

function RecipeCard({ template, onUse }: { template: Template; onUse: () => void }) {
  const channelMeta = TEMPLATE_CHANNELS.find((c) => c.id === template.channel)
  return (
    <article className="card card-hover group flex flex-col">
      {/*
        Visual plate. Portrait, because every recipe is 9:16 and every preview
        is generated at that ratio — a landscape plate center-crops a wide band
        out of a tall frame, which lands on the torso and cuts the subject's
        head off. 3:4 keeps the grid dense enough for 19 tiles while showing
        the part of the frame the shot is actually about.
      */}
      <div className="aspect-[3/4] relative overflow-hidden bg-bg-2">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, rgba(212,255,58,0.12), transparent 60%), radial-gradient(circle at 80% 75%, rgba(255,138,92,0.10), transparent 60%)',
          }}
          aria-hidden
        />
        <TemplatePreview template={template} />
        <div className="absolute top-3 left-3">
          <div className="pill !bg-black/55 !border-white/20 !text-white backdrop-blur-sm">
            <span>{channelMeta?.label ?? template.channel}</span>
          </div>
        </div>
        <div className="absolute bottom-3 right-3">
          <div className="pill !bg-black/55 !border-white/20 !text-white backdrop-blur-sm">
            <span>{template.aspectRatio}</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        <h2
          className="text-[1.22rem] text-text leading-tight mb-1"
          style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.02em' }}
        >
          {template.name}
        </h2>
        <p className="text-text-3 text-[0.86rem] mb-3">
          {template.description}
        </p>
        <p className="h-italic text-text-2 text-[1.05rem] leading-snug line-clamp-3 flex-1">
          “{template.prompt.slice(0, 160)}{template.prompt.length > 160 ? '…' : ''}”
        </p>

        <div className="mt-5 pt-4 border-t border-line flex items-center justify-between">
          {/* Editing and photo recipes need an upload to mean anything, so say
              so on the card rather than letting the user find out after they
              click. */}
          <span className="text-text-3 text-[0.82rem]">
            {SOURCE_KIND_CARD_LABEL[template.sourceKind ?? 'prompt']}
          </span>
          <button onClick={onUse} className="btn-accent" style={{ padding: '0.5rem 0.95rem', fontSize: '0.86rem' }}>
            Use →
          </button>
        </div>
      </div>
    </article>
  )
}

function FilterPill({
  active, onClick, children, title, count,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  title?: string
  count: number
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-[0.86rem] transition-colors ${
        active
          ? 'bg-accent/15 border-accent/40 text-accent'
          : 'bg-white/[0.03] border-line-2 text-text-2 hover:text-text hover:border-line-3'
      }`}
    >
      <span>{children}</span>
      <span className="t-mono text-[0.72rem] text-text-3">{count}</span>
    </button>
  )
}
